import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defaultTo, isNil } from 'lodash';
import React from 'react';

import { useAppForm } from '@/app/lib/tanstack-form';
import { BillingSidebar } from '@/billing/components/billing-sidebar';
import { PaymentMethodSelect } from '@/billing/components/payment-method-select';
import { UnableToPay } from '@/billing/components/unable-to-pay';
import { useCardDetailsForm } from '@/billing/hooks/use-card-details-form';
import { useCheckDetailsForm } from '@/billing/hooks/use-check-details-form';
import { PaymentMethodValue } from '@/billing/models/payment-type';
import { useSetupAutomaticPayments } from '@/billing/queries';
import { Button, ButtonGroup } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { Setting, SettingContent, SettingName, Settings, SettingsSet } from '@/settings/components/settings';
import { getAccountLabel } from '@/shared/accounts';
import { useUserAccounts } from '@/shared/accounts/queries';
import { ContentSection } from '@/shared/components/content-section';
import { RequiresAccounts } from '@/shared/components/requires-accounts';

export const Route = createFileRoute('/billing/automatic-payments/new')({
  beforeLoad: async ctx => ({
    user: await ctx.context.ensureUser()
  }),
  head: () => ({
    meta: [
      { title: 'Add Automatic Payment | myTalquin Member Portal' }
    ]
  }),
  component: AddAutomaticPaymentPage
});

function AddAutomaticPaymentPage() {
  const { user } = Route.useRouteContext();
  const { accounts } = user;
  const hasAnyAccounts = accounts.length > 0;
  const { data: accountDetails } = useUserAccounts();

  const noMoreCards = accountDetails?.some(account => account.no_more_charge);
  const noMoreChecks = accountDetails?.some(account => account.no_more_checks);
  const isUnableToPay = noMoreCards && noMoreChecks;

  const [validationDialog, setValidationDialog] = React.useState<{ title: string; description: string; open: boolean }>({ title: '', description: '', open: false });
  const navigate = useNavigate();

  const accountsWithoutAutomaticPayments = accountDetails.filter(account => !account.automatic_payment);

  const setupAutomaticPaymentsMutation = useSetupAutomaticPayments({
    onSuccess: async () => {
      await navigate({ to: '/billing/automatic-payments' });
    }
  });

  const parentForm = useAppForm({
    defaultValues: {
      paymentType: null as PaymentMethodValue,
      accounts: accountsWithoutAutomaticPayments.map(account => ({
        accountNumber: account.account_number,
        selected: false
      }))
    }
  });

  const newCardDetailsForm = useCardDetailsForm();
  const newCheckDetailsForm = useCheckDetailsForm();

  const handleSaveClick = async () => {
    await parentForm.handleSubmit();
    const { paymentType, accounts } = parentForm.state.values;
    const selectedAccounts = accounts.filter(account => account.selected);

    if (selectedAccounts.length === 0) {
      setValidationDialog({
        title: 'No Accounts Selected',
        description: 'Please select at least one account',
        open: true
      });
      return;
    }

    if (isNil(paymentType)) {
      setValidationDialog({
        title: 'No Payment Method Selected',
        description: 'Please select a payment method',
        open: true
      });
      return;
    }

    const accountNumbers = selectedAccounts.map(account => account.accountNumber);

    if (paymentType.saved) {
      const id = paymentType.id;
      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'saved',
        paymentMethod: { id }
      });

      return;
    }

    if (paymentType.type === 'card') {
      await newCardDetailsForm.handleSubmit();
      if (!newCardDetailsForm.state.isValid) return;

      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'card',
        paymentMethod: newCardDetailsForm.state.values
      });

      return;
    }

    if (paymentType.type === 'check') {
      await newCheckDetailsForm.handleSubmit();
      if (!newCheckDetailsForm.state.isValid) return;

      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'check',
        paymentMethod: newCheckDetailsForm.state.values
      });

      return;
    }
  };

  const closeValidationDialog = () => {
    setValidationDialog({ title: '', description: '', open: false });
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <BillingSidebar active="automatic-payments"/>
      <ContentSection bg="white">
        <Heading>Add Automatic Payment</Heading>

        <RequiresAccounts
          hasAnyAccounts={hasAnyAccounts}
          isUnavailable={isUnableToPay}
          unavailableState={<UnableToPay/>}
        >
          <Text>
            Select the account(s) which you would like to add this automatic payment and then “Select Payment Method” to either choose a Payment Method or enter a new one.
          </Text>

          <Settings>
            <SettingsSet>
              <Setting>
                <SettingName>Accounts</SettingName>
                <SettingContent>
                  <parentForm.AppField name="accounts" mode="array">
                    {accountsField => (
                      <CheckboxGroup>
                        {accountsField.state.value.map((value, idx) => {
                          const account = accounts.find(it => it.account_number === value.accountNumber)!;

                          return (
                            <parentForm.AppField name={`accounts[${idx}].selected`} key={value.accountNumber}>
                              {accountField => (
                                <CheckboxField>
                                  <accountField.FormCheckbox />
                                  <Label>{getAccountLabel(account)}</Label>
                                </CheckboxField>
                              )}
                            </parentForm.AppField>
                          );
                        })}
                      </CheckboxGroup>
                    )}
                  </parentForm.AppField>
                </SettingContent>
              </Setting>
            </SettingsSet>
          </Settings>

          <h3 className="font-bold mt-4">
            Payment Method
          </h3>

          <parentForm.AppField name="paymentType">
            {({ state: { value }, handleChange }) => (
              <PaymentMethodSelect
                className="my-4"
                paymentMethod={value}
                onPaymentMethodChange={handleChange}
                cardDetailsForm={newCardDetailsForm}
                checkDetailsForm={newCheckDetailsForm}
                noMoreCards={noMoreCards}
                noMoreChecks={noMoreChecks}
              />
            )}
          </parentForm.AppField>

          <Text>
            By submitting Automatic Payment online: I authorize Talquin to draw monthly bank drafts on my bank account and/or credit or debit card for the payment of my monthly electric, water, and/or wastewater bill(s). I understand I can discontinue participation in Automatic Payment by cancelling in myTalquin Member Portal prior to the next billing date. Talquin can terminate this agreement for any reason including account credit history or returned payments.
          </Text>

          <Text className="mt-2">
            I understand if funds are unavailable at the time of payment, the amount will be charged back to my account(s) and a fee will be assessed.
          </Text>

          <ButtonGroup>
            <Button to="/billing/automatic-payments" plain>Cancel</Button>
            <Button
              onClick={handleSaveClick}
              disabled={setupAutomaticPaymentsMutation.isPending}
            >
              {setupAutomaticPaymentsMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </ButtonGroup>
        </RequiresAccounts>
      </ContentSection>

      <Dialog open={validationDialog.open} onClose={closeValidationDialog}>
        <DialogTitle>{defaultTo(validationDialog.title, 'Validation Error')}</DialogTitle>
        <DialogDescription>{defaultTo(validationDialog.description, 'Please review your submission and try again.')}</DialogDescription>
        <DialogActions>
          <Button plain onClick={closeValidationDialog}>OK</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
